// Global
import { Splide as SplideCore } from '@splidejs/splide';
import { Intersection } from '@splidejs/splide-extension-intersection';
import { Splide, SplideProps, SplideSlide, SplideTrack } from '@splidejs/react-splide';
import '@splidejs/splide/css';
import { JSX, useRef } from 'react';
import { tv } from 'tailwind-variants';

// Local
import { Lists } from '.generated/Feature.CW.model';
import { withStandardComponentWrapper } from 'helpers/HOC';
import { SvgIcon } from 'helpers/SvgIcon';
import useDictionary from 'lib/hooks/useDictionary';
import { PlaceholderWrapper } from 'helpers/SitecoreWrappers/PlaceholderWrapper/PlaceholderWrapper';
import useIsEditing from 'lib/hooks/useIsEditing';
import { EditingHelpText } from 'helpers/Editing/EditingHelpText';

export type CarouselProps = Lists.CarouselItem_Component & {
  params: {
    autoLoop: '0' | '1' | undefined;
    autoPlay: '0' | '1' | undefined;
  };
};

const SPLIDE_INTERVAL = 3000;

const SPLIDE_SPEED = 300;

const SPLIDE_STEPPED_PROGRESS = false;

const Carousel = (props: CarouselProps): JSX.Element => {
  const { DynamicPlaceholderId, RenderingIdentifier, autoLoop, autoPlay } = props?.params || {};

  const boolAutoLoop = autoLoop === '1' ? true : false;
  const boolAutoPlay = autoPlay === '1' ? true : false;
  const phKey = `carousel-${DynamicPlaceholderId}`;

  const { getDictionaryValue } = useDictionary();

  const isEditing = useIsEditing();

  const splideOptions: SplideProps['options'] = {
    autoplay: 'pause',
    classes: {
      page: 'splide__pagination__page !rounded-full !border-1 !border-solid !border-black lg:!h-5 lg:!w-5 !h-3.5 !w-3.5',
    },
    gap: '.01rem',
    intersection: {
      inView: {
        autoplay: boolAutoPlay,
      },
      outView: {
        autoplay: false,
      },
    },
    interval: SPLIDE_INTERVAL,
    pagination: true,
    perMove: 1,
    perPage: 1,
    rewind: boolAutoLoop,
    speed: SPLIDE_SPEED,
    type: 'slide',
    width: '100%',
  };

  /*
   * REFS
   */

  const carouselProgressBarRef = useRef<HTMLDivElement>(null);

  /*
   * Event Handers
   */

  const onMove = (_instance: SplideCore, newIndex: number) => {
    if (!carouselProgressBarRef.current) return;
    if (SPLIDE_STEPPED_PROGRESS && newIndex !== 0) return;

    // On moving from the last slide to the first, temporarily disable the transition duration and set the progress bar's width to 0%.
    carouselProgressBarRef.current.style.transitionDuration = '0ms';
    carouselProgressBarRef.current.style.width = '0%';
  };

  const onMoved = (instance: SplideCore) => {
    if (!carouselProgressBarRef.current) return;

    const duration = String(SPLIDE_INTERVAL - SPLIDE_SPEED) + 'ms';
    const end = instance.Components.Controller.getEnd() + 1;
    const rate = Math.min((instance.index + 1) / end, 1);

    let width = String(100 * rate) + '%';

    if (!SPLIDE_STEPPED_PROGRESS) width = '100%';

    // Reset the transition duration and set the progress bar's width.
    carouselProgressBarRef.current.style.transitionDuration = duration;
    carouselProgressBarRef.current.style.width = width;
  };

  /*
   * Rendering
   */

  const {
    base,
    nextButton,
    nextButtonIcon,
    pagination,
    pauseButton,
    playButton,
    previousButton,
    previousButtonIcon,
    progressBarItem,
    progressBarWrapper,
    screenReader,
    slideArrows,
    slideControlButton,
    slideControls,
  } = TAILWIND_VARIANTS();

  if (isEditing) {
    return (
      <section data-component="authorable/shared/lists/carousel" id={RenderingIdentifier}>
        <EditingHelpText>
          Carousel rendered as flat list for ease of editing. Use preview to see carousel in action.
        </EditingHelpText>
        <PlaceholderWrapper
          name={phKey}
          rendering={props.rendering}
          // Wrap the component in a <Splide>.
          // If there are no components it won't render an empty carousel
          // It also ensures that when editing in Page Builder, it will show the help text outside of the <ul>
          render={(components) => (
            <div className={base()}>
              {components.map((comp, index) => (
                <div key={index} className="relative">
                  {comp}
                </div>
              ))}
            </div>
          )}
        />
      </section>
    );
  }
  return (
    <section data-component="authorable/shared/lists/carousel" id={RenderingIdentifier}>
      <PlaceholderWrapper
        name={phKey}
        rendering={props.rendering}
        // Wrap the component in a <Splide>.
        // If there are no components it won't render an empty carousel
        // It also ensures that when editing in Page Builder, it will show the help text outside of the <ul>
        render={(components) => (
          <div className={base()}>
            <Splide
              extensions={{ Intersection }}
              hasTrack={false}
              onMounted={onMoved}
              onMove={onMove}
              onMoved={onMoved}
              options={splideOptions}
            >
              <SplideTrack>
                {components.map((comp, index) => (
                  <SplideSlide key={index}>{comp}</SplideSlide>
                ))}
              </SplideTrack>
              <div className={slideArrows()}>
                <button className={previousButton()}>
                  <span className={screenReader()}>{getDictionaryValue('PreviousSlide')}</span>
                  <SvgIcon className={previousButtonIcon()} icon="arrow-right" size="md" />
                </button>
                <button className={nextButton()}>
                  <span className={screenReader()}>{getDictionaryValue('NextSlide')}</span>
                  <SvgIcon className={nextButtonIcon()} icon="arrow-right" size="md" />
                </button>
              </div>
              {boolAutoPlay && (
                <div className={progressBarWrapper()}>
                  <div className={progressBarItem()} ref={carouselProgressBarRef} />
                </div>
              )}
              <ul className={pagination()}></ul>
              {boolAutoPlay && (
                <div className={slideControls()}>
                  <button className={slideControlButton()} type="button">
                    <span className={playButton()}>
                      <span className={screenReader()}>{getDictionaryValue('PlaySlideshow')}</span>
                      <SvgIcon icon="play" size="sm" />
                    </span>
                    <span className={pauseButton()}>
                      <span className={screenReader()}>{getDictionaryValue('PauseSlideshow')}</span>
                      <SvgIcon icon="pause" size="sm" />
                    </span>
                  </button>
                </div>
              )}
            </Splide>
          </div>
        )}
      />
    </section>
  );
};

export const Default = withStandardComponentWrapper(Carousel, false);

const TAILWIND_VARIANTS = tv({
  slots: {
    base: ['overflow-hidden', 'relative'],
    nextButton: [
      'splide__arrow',
      'splide__arrow--next',
      '!bg-transparent',
      '!border-none',
      '!opacity-100',
      '!shadow-none',
    ],
    nextButtonIcon: ['fill-theme-black-800', 'opacity-50'],
    pagination: [
      '!bottom-6',
      '!left-1/2',
      'space-x-1',
      'splide__pagination',
      'transform',
      '-translate-x-1/2',
      '!lg:absolute',
      'lg:!bottom-12',
    ],
    pauseButton: ['splide__toggle__pause'],
    playButton: ['splide__toggle__play'],
    previousButton: [
      'splide__arrow',
      'splide__arrow--prev',
      '!bg-transparent',
      '!border-none',
      '!opacity-100',
      '!shadow-none',
    ],
    previousButtonIcon: ['fill-theme-black-800', 'opacity-50'],
    progressBarItem: ['custom__splide__progress__bar'],
    progressBarWrapper: ['custom__splide__progress'],
    screenReader: ['sr-only'],
    slideArrows: ['splide__arrows'],
    slideControlButton: ['splide__toggle', 'icon-hover-focus-rounded'],
    slideControls: ['absolute', 'bottom-2', 'right-6', 'text-gray', 'lg:bottom-8', 'lg:right-14'],
  },
});
